<template>
  <button
    class="cart-sidebar-item__remove"
    :disabled="disabled"
    :aria-label="substitute(cartSidebarContent.removeItemAriaLabelTemplate, { ItemName: title })"
    @click="$emit('remove')"
  >
    <FontAwesomeIcon :icon="appIcons.farXmark" />
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import useAppIcons from '@/composables/appIcons'
import { substitute } from '@/composables/contentful'

import { cartSidebarContent } from '@/content/cartSidebar'

/**
 * Remove control for a paid cart-flyout row — Figma `15980:29655`, a 24px box with an 18px Font
 * Awesome Pro Regular glyph.
 *
 * Presentational and props-driven: the only row type with an action is a paid bundle (reward rows and
 * the Free Shipping row have no control at all, Gap 4), and both the live row and the debug harness
 * need the same button. It was previously inline in `AppCartSidebarBundleItem`; it moved out so the
 * harness renders the real control rather than a lookalike.
 */
export default defineComponent({
  name: 'AppCartSidebarRemoveButton',
  props: {
    /** Row title — used only to name the button for assistive technology. */
    title: {
      type: String,
      required: true
    },
    /** True while a cart mutation is in flight, so rapid taps can't queue duplicate removals. */
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['remove'],
  setup() {
    return { ...useAppIcons(), cartSidebarContent, substitute }
  }
})
</script>

<style scoped lang="scss">
.cart-sidebar-item__remove {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  // Sizes the Font Awesome glyph, which renders at 1em.
  font-size: 1.125rem; // 18px
  line-height: 1;
  color: $cady-color-black;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}
</style>
